import { midiNoteNumberFromFrequency } from '../note/note-frequencies.js'
import { boundaryCents, boundaryHoldMs, gapMs, scoopMs } from './constants.js'
import { interquartileMean, median, quantile } from './statistics.js'
import type { PitchFrame, SungNote } from './types.js'

export interface SegmentOptions {
  boundaryCents?: number
  boundaryHoldMs?: number
  gapMs?: number
  scoopMs?: number
  /**
   * The capture's sample rate, which is what turns a frame's `onsetSampleOffset` into seconds.
   *
   * Optional, and deliberately without a default: the offset is in samples and a `PitchFrame`
   * carries no rate, so a guessed 48 kHz would be wrong by 8 % on a 44.1 kHz device - silently, and
   * in the one number the refinement exists to fix. Omit it and every onset is the first voiced
   * frame's timestamp, which is what this function did before the refinement existed.
   * @see docs/superpowers/specs/2026-09-08-melody-plugin-design.md §8
   */
  sampleRate?: number
}

/**
 * Trim a note's measured pitches to a whole number of vibrato cycles.
 *
 * The median rejects a vibrato's extremes — which is §5.2's stated reason for choosing it — but
 * it does not reject the *bias* of a modulation cut off part-way through a cycle. A 500 ms note
 * leaves 400 ms after the onset scoop, which is 2.2 cycles of a 5.5 Hz vibrato, and the leftover
 * fifth of a cycle pulls the median off the note's centre by an amount set by whatever phase the
 * note happened to end on: measured -8.46, -15.31 and -10.80 cents on notes sung dead centre
 * (Glenn's fixture, 2026-09-07), against -0.33, -0.86 and +0.36 on 1.5 s notes of the same
 * vibrato. The error scales as one over the number of cycles measured, so it is worst on exactly
 * the short notes §6's exercise ladder is built from, and its sign follows nothing the singer
 * did. §5.6 forbids charging a singer for their vibrato; this is that charge, arriving through
 * §5.4.
 *
 * The fix is to measure over whole periods. Take the residual against the note's own median,
 * find where it crosses zero, and keep the frames between the first and last crossing *in the
 * same direction* — rising-to-rising or falling-to-falling spans an integer number of periods,
 * so the leftover fraction is gone. With fewer than two like-direction crossings there is no
 * cycle to align to (a steady note, or less than one period of vibrato) and the full window is
 * the right answer. On a steady note the residual has no crossings worth trimming and every
 * frame is kept, so nothing that is not modulated is affected.
 *
 * Trimming is only half the fix, and on its own it is not enough: a *median* over the trimmed
 * window still sits ~4 c off, because at 8.5 frames per period the remaining error is the
 * sampling grid rather than the truncation, and it does not shrink as the note lengthens. The
 * centre estimate has to be an `interquartileMean` — @see ./statistics.js for that measurement.
 *
 * @see docs/superpowers/specs/2026-09-06-vocal-pitch-training-design.md §5.2, §5.6
 */
export const wholePeriodFrames = (midis: number[]): number[] => {
  if (midis.length < 3) {
    return midis
  }
  const centre = median(midis)

  /** Index of the first frame on the new side of the median, per crossing direction. */
  const rising: number[] = []
  const falling: number[] = []
  let previousSign = 0
  for (let i = 0; i < midis.length; i++) {
    const sign = Math.sign(midis[i] - centre)
    if (sign === 0) {
      continue
    }
    if (previousSign < 0 && sign > 0) {
      rising.push(i)
    }
    if (previousSign > 0 && sign < 0) {
      falling.push(i)
    }
    previousSign = sign
  }

  /** [first, last) — excluding the last crossing keeps the span an exact number of periods. */
  const spanOf = (crossings: number[]) =>
    crossings.length >= 2 ? { from: crossings[0], to: crossings[crossings.length - 1] } : null

  const spans = [spanOf(rising), spanOf(falling)].filter(span => span !== null)
  if (spans.length === 0) {
    return midis
  }

  /** Prefer whichever direction spans more cycles — more periods, less sampling error. */
  const best = spans.reduce((chosen, span) => (span.to - span.from > chosen.to - chosen.from ? span : chosen))
  return midis.slice(best.from, best.to)
}

/**
 * Close a candidate note. A candidate with no post-scoop frames is shorter than the onset
 * portamento: once the scoop is discarded there is nothing left to measure, so it is dropped
 * rather than reported as a note whose pitch is its own scoop.
 */
const closeNote = (frames: PitchFrame[], scoopSeconds: number, sampleRate?: number): SungNote | null => {
  /*
   * The onset, to the sample, when the worklet found it. A frame's `time` can only ever be a point
   * on the analysis grid - frames arrive one per hop - so an onset taken from it is displaced by
   * however the performance fell against that clock, uniformly on ±half a hop and by nothing the
   * musician did. The worklet holds the samples and reports the foot of the attack; this is where
   * that answer is used. Absent either the offset or a rate to divide it by, the frame timestamp
   * stands, exactly as before.
   *
   * The scoop is then measured from the REFINED start: §5.2's onset portamento is a property of the
   * performance, so its window moves with the note, not with the analysis clock.
   */
  const offset = frames[0].onsetSampleOffset
  const startTime =
    sampleRate !== undefined && offset !== undefined ? frames[0].time + offset / sampleRate : frames[0].time

  const measured = frames.filter(frame => frame.time - startTime >= scoopSeconds)
  if (measured.length === 0) {
    return null
  }
  const midis = measured.map(frame => midiNoteNumberFromFrequency(frame.frequency))

  /**
   * Pitch is measured over whole vibrato periods; spread is measured over the whole note. The
   * two want different windows: the centre estimate needs an integer number of cycles to be
   * unbiased, while the IQR is reporting how far the note moved in total and has no reason to
   * discard the ends.
   */
  const midi = interquartileMean(wholePeriodFrames(midis))
  const spread = [...midis].sort((a, b) => a - b)

  return {
    startTime,
    endTime: frames[frames.length - 1].time,
    midi,
    cents: (midi - Math.round(midi)) * 100,
    iqrCents: (quantile(spread, 0.75) - quantile(spread, 0.25)) * 100,
  }
}

/**
 * Group voiced frames into notes (§5.2).
 *
 * Input is the output of `voicedFrames` — unvoiced frames are already gone, so an unvoiced gap
 * shows up here as a discontinuity in `time`.
 *
 * Boundary detection deliberately ignores the onset scoop: the running median is taken over the
 * post-scoop frames only, so a singer sliding a whole tone into a note cannot trip the 50-cent
 * boundary against their own scoop and split the note they have just started.
 */
export const segmentNotes = (frames: PitchFrame[], options: SegmentOptions = {}): SungNote[] => {
  const {
    boundaryCents: boundary = boundaryCents,
    boundaryHoldMs: hold = boundaryHoldMs,
    gapMs: gap = gapMs,
    scoopMs: scoop = scoopMs,
    sampleRate,
  } = options

  const scoopSeconds = scoop / 1000
  const gapSeconds = gap / 1000
  const holdSeconds = hold / 1000

  const notes: SungNote[] = []
  /** Frames of the note being built, and the post-scoop subset that votes on its median. */
  let current: PitchFrame[] = []
  let measured: number[] = []
  /** Frames off the running median that may yet turn out to be the start of a new note. */
  let pending: PitchFrame[] = []

  const flush = () => {
    if (current.length > 0) {
      const note = closeNote(current, scoopSeconds, sampleRate)
      if (note) {
        notes.push(note)
      }
    }
    current = []
    measured = []
    pending = []
  }

  /** Start a new note from the frames that were pending when the boundary was declared. */
  const restart = (seed: PitchFrame[]) => {
    flush()
    for (const frame of seed) {
      current.push(frame)
      if (frame.time - seed[0].time >= scoopSeconds) {
        measured.push(midiNoteNumberFromFrequency(frame.frequency))
      }
    }
  }

  for (const frame of frames) {
    const previous = pending[pending.length - 1] ?? current[current.length - 1]
    if (previous && frame.time - previous.time >= gapSeconds) {
      const seed = [...pending]
      restart(seed.length > 0 ? [...seed, frame] : [frame])
      continue
    }

    if (current.length === 0) {
      current.push(frame)
      continue
    }

    if (frame.time - current[0].time < scoopSeconds) {
      current.push(frame)
      continue
    }

    const midi = midiNoteNumberFromFrequency(frame.frequency)
    if (measured.length === 0) {
      current.push(frame)
      measured.push(midi)
      continue
    }

    if (Math.abs(midi - median(measured)) * 100 < boundary) {
      /** Back on the note: whatever was pending was a wobble, not a move. */
      for (const held of [...pending, frame]) {
        current.push(held)
        measured.push(midiNoteNumberFromFrequency(held.frequency))
      }
      pending = []
      continue
    }

    pending.push(frame)
    /** A duration, not a count: a vibrato excursion is several frames but never this long. */
    if (frame.time - pending[0].time >= holdSeconds) {
      restart(pending)
    }
  }

  /** Whatever is still pending never held long enough to be its own note. */
  current.push(...pending)
  pending = []
  flush()

  return notes
}
